import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { authApi } from '@/services/api';
import { FileText, Save, Loader2, CheckCircle, Info } from 'lucide-react';

export default function UAEIntegrationTab() {
  const qc = useQueryClient();
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState('');

  const { data: profile, isLoading } = useQuery({
    queryKey: ['profile'],
    queryFn: async () => {
      const res = await authApi.getCompanyProfile();
      return res.data.message;
    },
  });

  const [form, setForm] = useState({
    uae_trn: '',
    uae_company_name_ar: '',
    uae_asp_provider: '',
    uae_asp_reference: '',
  });

  useEffect(() => {
    if (profile) {
      setForm({
        uae_trn: profile.uae_trn || profile.tax_registration_id || '',
        uae_company_name_ar: profile.uae_company_name_ar || profile.company_name_ar || '',
        uae_asp_provider: profile.uae_asp_provider || '',
        uae_asp_reference: profile.uae_asp_reference || '',
      });
    }
  }, [profile]);

  const saveMutation = useMutation({
    mutationFn: (data: Record<string, unknown>) => authApi.updateCompanyProfile(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['profile'] });
      setSaveSuccess(true);
      setSaveError('');
      setTimeout(() => setSaveSuccess(false), 4000);
    },
    onError: (err: { response?: { data?: { message?: string } }; message?: string }) => {
      setSaveError(err.response?.data?.message || err.message || 'Failed to save UAE settings');
    },
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
      </div>
    );
  }

  if ((profile?.country || '') !== 'AE') {
    return (
      <div className="rounded-xl border border-amber-200 bg-amber-50 p-6 text-sm text-amber-900">
        UAE e-invoicing is only available for United Arab Emirates. Set your company country to UAE to configure ASP settings.
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-3xl">
      <div className="bg-gradient-to-r from-red-700 to-red-900 text-white rounded-xl p-5 flex items-center gap-4">
        <FileText className="w-10 h-10 shrink-0 opacity-90" />
        <div>
          <h2 className="text-lg font-bold">UAE E-Invoicing (ASP)</h2>
          <p className="text-red-100 text-sm">PINT AE export handoff — configure ASP partner before live transmission</p>
        </div>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex gap-3">
        <Info className="w-5 h-5 text-blue-600 shrink-0 mt-0.5" />
        <div className="text-sm text-blue-800">
          <p className="font-semibold">Readiness mode</p>
          <p>Sale Desk exports PINT AE-oriented JSON for your Accredited Service Provider. Direct FTA submission requires an ASP partner (pilot Jul 2026; large taxpayers mandatory Jan 2027).</p>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 p-6 space-y-4">
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">TRN</label>
            <input className="input-field" value={form.uae_trn}
              onChange={(e) => setForm({ ...form, uae_trn: e.target.value })} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Company Name (Arabic)</label>
            <input className="input-field" dir="rtl" value={form.uae_company_name_ar}
              onChange={(e) => setForm({ ...form, uae_company_name_ar: e.target.value })} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">ASP Provider</label>
            <input className="input-field" value={form.uae_asp_provider}
              onChange={(e) => setForm({ ...form, uae_asp_provider: e.target.value })} placeholder="Accredited Service Provider" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">ASP Reference / Account ID</label>
            <input className="input-field" value={form.uae_asp_reference}
              onChange={(e) => setForm({ ...form, uae_asp_reference: e.target.value })} />
          </div>
        </div>

        <button type="button" onClick={() => saveMutation.mutate(form)} disabled={saveMutation.isPending}
          className="btn-primary flex items-center gap-2">
          {saveMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          Save UAE Settings
        </button>
      </div>

      {saveSuccess && (
        <div className="p-4 rounded-lg bg-green-50 text-green-800 text-sm flex items-center gap-2">
          <CheckCircle className="w-5 h-5" /> UAE e-invoicing settings saved
        </div>
      )}
      {saveError && (
        <div className="p-4 rounded-lg bg-red-50 text-red-800 text-sm">{saveError}</div>
      )}
    </div>
  );
}
